import React, { useState, useEffect } from 'react';
import { useApi } from '../hooks/useApi.js';
import { useRoles } from '../hooks/useRoles.js';
import EmployeeModal from '../components/EmployeeModal.jsx';

export default function EmployeesPage() {
  const api = useApi();
  const { roleNames, skillNames } = useRoles();
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [filter, setFilter] = useState({ role: '', status: 'active', search: '' });

  useEffect(() => {
    loadEmployees();
  }, []);

  async function loadEmployees() {
    setLoading(true);
    try {
      const data = await api.get('/employees');
      setEmployees(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  function handleAdd() {
    setEditingEmployee(null);
    setShowModal(true);
  }

  function handleEdit(emp) {
    setEditingEmployee(emp);
    setShowModal(true);
  }

  async function handleDeactivate(emp) {
    if (!window.confirm(`Deactivate ${emp.name}? They will no longer be included in new rosters.`)) return;
    try {
      await api.delete(`/employees/${emp.id}`);
      setEmployees(prev => prev.map(e => e.id === emp.id ? { ...e, is_active: false } : e));
    } catch (err) {
      alert(err.message);
    }
  }

  async function handleReactivate(emp) {
    try {
      const updated = await api.put(`/employees/${emp.id}`, { is_active: true });
      setEmployees(prev => prev.map(e => e.id === emp.id ? updated : e));
    } catch (err) {
      alert(err.message);
    }
  }

  function handleSaved(emp) {
    setEmployees(prev => {
      const idx = prev.findIndex(e => e.id === emp.id);
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = emp;
        return next;
      }
      return [emp, ...prev];
    });
    setShowModal(false);
  }

  const filtered = employees.filter(e => {
    if (filter.status === 'active' && !e.is_active) return false;
    if (filter.status === 'inactive' && e.is_active) return false;
    if (filter.role && e.role !== filter.role) return false;
    if (filter.search && !e.name.toLowerCase().includes(filter.search.toLowerCase())) return false;
    return true;
  });

  const ROLES = roleNames.length > 0 ? roleNames : ['kitchen', 'front_of_house', 'cashier', 'manager', 'bar', 'delivery'];

  function roleLabel(r) {
    return r.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  }

  function employmentLabel(t) {
    return t?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || '—';
  }

  return (
    <div className="container">
      <div className="page-header">
        <div>
          <h1 className="page-title">Employees</h1>
          <p className="page-subtitle">{employees.filter(e => e.is_active).length} active staff members</p>
        </div>
        <button className="btn btn-primary" onClick={handleAdd}>+ Add Employee</button>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      {/* Filters */}
      <div className="emp-filter-bar mb-4">
        <div className="emp-filter-search">
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ color: 'var(--text-muted)', flexShrink: 0 }}><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
          <input
            type="text"
            placeholder="Search by name..."
            value={filter.search}
            onChange={e => setFilter(prev => ({ ...prev, search: e.target.value }))}
          />
        </div>
        <div className="emp-filter-divider" />
        <select
          value={filter.role}
          onChange={e => setFilter(prev => ({ ...prev, role: e.target.value }))}
        >
          <option value="">All Roles</option>
          {ROLES.map(r => <option key={r} value={r}>{roleLabel(r)}</option>)}
        </select>
        <div className="emp-filter-divider" />
        <select
          value={filter.status}
          onChange={e => setFilter(prev => ({ ...prev, status: e.target.value }))}
        >
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
          <option value="all">All</option>
        </select>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 40 }}>
          <span className="spinner spinner-dark" style={{ width: 28, height: 28 }} />
        </div>
      ) : filtered.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon">👥</div>
          <h3>No employees found</h3>
          <p>Add your first team member to get started.</p>
          <button className="btn btn-primary" onClick={handleAdd}>Add Employee</button>
        </div>
      ) : (
        <div className="card" style={{ padding: '12px 16px', overflow: 'hidden' }}>
        <div className="table-wrap table-wrap-nostack table-wrap-md-cards" style={{ borderRadius: 0, border: 'none', margin: '0 -16px' }}>
          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Role</th>
                <th>Employment</th>
                <th>Hours/wk</th>
                <th>Hourly Rate</th>
                <th>Skills</th>
                <th>Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(emp => (
                <tr key={emp.id}>
                  <td data-label="Name">
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontWeight: 600 }}>{emp.name}</div>
                      {emp.email && <div className="text-muted text-sm">{emp.email}</div>}
                    </div>
                  </td>
                  <td data-label="Role">
                    <span className="badge badge-primary">{roleLabel(emp.role)}</span>
                  </td>
                  <td data-label="Employment">
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      {employmentLabel(emp.employment_type)}
                      {emp.is_award_exempt && (
                        <span className="badge badge-secondary" style={{ fontSize: 10 }}>Exempt</span>
                      )}
                    </div>
                  </td>
                  <td data-label="Hours/wk">
                    {emp.min_hours_per_week > 0
                      ? `${emp.min_hours_per_week}–${emp.max_hours_per_week}`
                      : `up to ${emp.max_hours_per_week}`}
                  </td>
                  <td data-label="Rate">{emp.hourly_rate ? `$${parseFloat(emp.hourly_rate).toFixed(2)}/h` : '—'}</td>
                  <td data-label="Skills">
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                      {(emp.skills || []).slice(0, 3).map(s => (
                        <span key={s} className="badge badge-secondary">
                          {s.replace(/_/g, ' ')}
                        </span>
                      ))}
                      {(emp.skills || []).length > 3 && (
                        <span className="text-muted text-sm">+{emp.skills.length - 3}</span>
                      )}
                    </div>
                  </td>
                  <td data-label="Status">
                    <span className={`badge badge-${emp.is_active ? 'success' : 'secondary'}`}>
                      {emp.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button className="btn btn-ghost btn-sm" onClick={() => handleEdit(emp)}>Edit</button>
                      {emp.is_active ? (
                        <button className="btn btn-ghost btn-sm" style={{ color: 'var(--color-danger)' }} onClick={() => handleDeactivate(emp)}>
                          Deactivate
                        </button>
                      ) : (
                        <button className="btn btn-ghost btn-sm" style={{ color: 'var(--color-success)' }} onClick={() => handleReactivate(emp)}>
                          Reactivate
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        </div>
      )}

      {showModal && (
        <EmployeeModal
          employee={editingEmployee}
          onClose={() => setShowModal(false)}
          onSaved={handleSaved}
          roleNames={roleNames}
          skillNames={skillNames}
        />
      )}
    </div>
  );
}
